import { useCallback, useEffect, useState } from 'react'
import { getLatestReleaseNote } from '@/client/sdk.gen'
import type { ReleaseNotePublicResponse } from '@/client/types.gen'
import { useAuth } from '@/context/AuthContext'

export function useWhatsNew() {
	const { user, loading } = useAuth()
	const [note, setNote] = useState<ReleaseNotePublicResponse | null>(null)
	const [shouldShow, setShouldShow] = useState(false)

	const fetchLatest = useCallback(
		async (isActive: () => boolean) => {
			if (!user?.id) {
				if (isActive()) {
					setNote(null)
					setShouldShow(false)
				}
				return
			}

			try {
				const response = await getLatestReleaseNote()
				if (!isActive()) {
					return
				}
				const payload = response.data
				if (!payload?.note || payload.already_seen) {
					setNote(null)
					setShouldShow(false)
				} else {
					setNote(payload.note)
					setShouldShow(true)
				}
			} catch {
				if (isActive()) {
					setNote(null)
					setShouldShow(false)
				}
			}
		},
		[user?.id]
	)

	useEffect(() => {
		if (loading) {
			return
		}
		// Guard against a stale in-flight response resolving after the account
		// switches (user.id changes) and showing the previous user's note.
		let cancelled = false
		void fetchLatest(() => !cancelled)
		return () => {
			cancelled = true
		}
	}, [loading, fetchLatest])

	return {
		note,
		shouldShow,
		dismiss: () => setShouldShow(false),
	}
}
